/**
 * Centralized Configuration for AightBet Frontend
 * ================================================
 * Single source of truth for all environment-based configuration
 */

interface Config {
  // API Configuration
  api: {
    baseUrl: string;
    wsUrl: string;
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
  };
  
  // Supabase Configuration
  supabase: {
    url: string;
    anonKey: string;
    persistSession: boolean;
    autoRefreshToken: boolean;
    detectSessionInUrl: boolean;
  };
  
  // Authentication
  auth: {
    jwtSecret?: string;
    domain: string;
    tokenStorageKey: string;
    refreshTokenKey: string;
    sessionDuration: number; // in milliseconds
  };
  
  // Environment
  environment: {
    name: 'development' | 'staging' | 'production';
    isDevelopment: boolean;
    isStaging: boolean;
    isProduction: boolean;
    version: string;
    buildTime: string;
  };
  
  // KAN-120: build-time feature flags moved to `./featureFlags.tsx`
  // (FEATURE_FLAGS singleton). Don't add new flags here.

  // Monitoring
  monitoring: {
    sentryDsn?: string;
    sentryEnvironment?: string;
    enableErrorReporting: boolean;
    enablePerformanceMonitoring: boolean;
  };

  // Launch Marketing Campaign — donation rail.
  // `donatePaymentLinkUrl` is a Stripe Payment Link (custom-amount, no
  // recurring). Empty string = donations not yet active; /donate renders a
  // tasteful "coming soon" placeholder instead of a broken button.
  marketing: {
    donatePaymentLinkUrl: string;
  };
  
  // API Endpoints
  endpoints: {
    // Games
    games: string;
    liveGames: string;
    gameDetails: (id: string) => string;
    oddsGames: string;
    gamesWithOdds: string;
    
    // Analysis
    analyzeGame: (gameId: string) => string;
    analyzeGameData: string;
    analyzeElite: (gameId: string) => string;
    analyzeBatch: string;
    
    // CFBD
    cfbdGames: string;
    cfbdStats: string;
    cfbdRankings: string;
    
    // Predictions
    latestPredictions: string;
    
    // Stats
    statsOverview: string;
    systemPerformance: string;
    
    // Betting
    bettingOpportunities: string;
    
    // Live Updates
    liveScoreUpdate: string;
    liveOddsUpdate: string;
    
    // AI
    aiBacktest: string;
    aiModelMetrics: string;
    aiPerformance: string;
    aiRealtimeUpdates: string;
    aiRetrain: string;
    
    // Health
    health: string;
  };
  
  // WebSocket Events
  wsEvents: {
    connect: string;
    disconnect: string;
    gameUpdate: string;
    oddsUpdate: string;
    predictionUpdate: string;
    error: string;
    heartbeat: string;
  };
}

// Environment detection - updated to work with Vercel proxy
const getEnvironment = (): 'development' | 'staging' | 'production' => {
  const env = import.meta.env.VITE_ENV;
  if (env) return env;

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  if (hostname.includes('staging') || hostname.includes('preview')) {
    return 'staging';
  }
  return 'production';
};

// Import centralized API configuration
import { getApiUrl as getCentralizedApiUrl, getWebSocketUrl as getCentralizedWebSocketUrl } from './api';

// Use centralized configuration functions
const getApiUrl = getCentralizedApiUrl;
const getWebSocketUrl = getCentralizedWebSocketUrl;

// Build configuration object
const buildConfig = (): Config => {
  const environment = getEnvironment();
  const apiBaseUrl = getApiUrl();
  const wsUrl = getWebSocketUrl();
  
  return {
    api: {
      baseUrl: apiBaseUrl,
      wsUrl,
      timeout: 60000, // 60 seconds
      retryAttempts: 2,
      retryDelay: 1000, // 1 second initial delay
    },
    
    supabase: {
      url: import.meta.env.VITE_SUPABASE_URL || '',
      anonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 
               import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    
    auth: {
      jwtSecret: import.meta.env.VITE_JWT_SECRET_KEY,
      domain: import.meta.env.VITE_AUTH_DOMAIN || window.location.hostname,
      tokenStorageKey: 'auth_token',
      refreshTokenKey: 'refresh_token',
      sessionDuration: 7 * 24 * 60 * 60 * 1000, // 7 days
    },
    
    environment: {
      name: environment,
      isDevelopment: environment === 'development',
      isStaging: environment === 'staging',
      isProduction: environment === 'production',
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      buildTime: import.meta.env.VITE_BUILD_TIME || new Date().toISOString(),
    },
    
    monitoring: {
      sentryDsn: import.meta.env.VITE_SENTRY_DSN,
      sentryEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT || environment,
      enableErrorReporting: environment === 'production',
      enablePerformanceMonitoring: environment === 'production',
    },

    marketing: {
      donatePaymentLinkUrl: import.meta.env.VITE_DONATE_PAYMENT_LINK_URL || '',
    },
    
    endpoints: {
      // Games
      games: `${apiBaseUrl}/api/games`,
      liveGames: `${apiBaseUrl}/api/games/live`,
      gameDetails: (id: string) => `${apiBaseUrl}/api/games/${id}`,
      oddsGames: `${apiBaseUrl}/api/odds-games`,
      gamesWithOdds: `${apiBaseUrl}/api/games-with-odds`,
      
      // Analysis
      analyzeGame: (gameId: string) => `${apiBaseUrl}/api/analyze/game/${gameId}`,
      analyzeGameData: `${apiBaseUrl}/api/analyze/game-data`,
      analyzeElite: (gameId: string) => `${apiBaseUrl}/api/analyze/elite/${gameId}`,
      analyzeBatch: `${apiBaseUrl}/api/analyze/batch`,
      
      // CFBD
      cfbdGames: `${apiBaseUrl}/api/cfbd/games`,
      cfbdStats: `${apiBaseUrl}/api/cfbd/stats`,
      cfbdRankings: `${apiBaseUrl}/api/cfbd/rankings`,
      
      // Predictions
      latestPredictions: `${apiBaseUrl}/api/predictions/latest`,
      
      // Stats
      statsOverview: `${apiBaseUrl}/api/stats/overview`,
      systemPerformance: `${apiBaseUrl}/api/system/performance`,
      
      // Betting
      bettingOpportunities: `${apiBaseUrl}/api/betting/opportunities`,
      
      // Live Updates
      liveScoreUpdate: `${apiBaseUrl}/api/live/score-update`,
      liveOddsUpdate: `${apiBaseUrl}/api/live/odds-update`,
      
      // AI
      aiBacktest: `${apiBaseUrl}/api/ai/backtest`,
      aiModelMetrics: `${apiBaseUrl}/api/ai/model-metrics`,
      aiPerformance: `${apiBaseUrl}/api/ai/performance`,
      aiRealtimeUpdates: `${apiBaseUrl}/api/ai/realtime-updates`,
      aiRetrain: `${apiBaseUrl}/api/ai/retrain`,
      
      // Health
      health: `${apiBaseUrl}/api/health`,
    },
    
    wsEvents: {
      connect: 'connect',
      disconnect: 'disconnect',
      gameUpdate: 'game_update',
      oddsUpdate: 'odds_update',
      predictionUpdate: 'prediction_update',
      error: 'error',
      heartbeat: 'heartbeat',
    },
  };
};

// Export singleton configuration
const config = buildConfig();

// Named exports for backward compatibility
export const API_BASE_URL = config.api.baseUrl;
export const WS_BASE_URL = config.api.wsUrl;
export const API_URL = config.api.baseUrl; // Alias for compatibility
export const WS_URL = config.api.wsUrl; // Alias for compatibility

// Export API endpoints for convenience
export const API_ENDPOINTS = {
  games: {
    list: config.endpoints.games,
    upcoming: `${config.api.baseUrl}/api/games/upcoming`,
    live: config.endpoints.liveGames,
    completed: `${config.api.baseUrl}/api/games/completed`,
    byId: (id: string) => config.endpoints.gameDetails(id),
  },
  predictions: {
    byGameId: (gameId: string) => `${config.api.baseUrl}/api/predictions/${gameId}`,
    generate: `${config.api.baseUrl}/api/predictions/generate`,
    history: `${config.api.baseUrl}/api/predictions/history`,
    batch: config.endpoints.analyzeBatch,
  },
  odds: {
    list: `${config.api.baseUrl}/api/odds`,
    byGameId: (gameId: string) => `${config.api.baseUrl}/api/odds/${gameId}`,
    history: (gameId: string) => `${config.api.baseUrl}/api/odds/${gameId}/history`,
    live: `${config.api.baseUrl}/api/odds/live`,
  },
};

// WebSocket events
export const WS_EVENTS = config.wsEvents;

export default config;
export type { Config };